import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@saas-kit/lib/auth';
import { logout } from '@saas-kit/lib/auth';
import { Tooltip } from '../Tooltip';

import { Brand } from './Brand';
import { LogOutIcon, SettingsIcon } from './icons';
import { SidebarGroup } from './SidebarGroup';
import { SidebarLink } from './SidebarLink';
import type { BrandInfo, NavEntry } from './types';

function filterVisible(
  nav: NavEntry[],
  perms: string[],
  isAdmin: boolean,
): NavEntry[] {
  const out: NavEntry[] = [];
  for (const entry of nav) {
    const passesSelf =
      !entry.permission || isAdmin || perms.includes(entry.permission);
    if (entry.children) {
      const visKids = filterVisible(entry.children, perms, isAdmin);
      if (passesSelf && visKids.length > 0) {
        out.push({ ...entry, children: visKids });
      }
    } else if (passesSelf) {
      out.push(entry);
    }
  }
  return out;
}

interface Props {
  brand: BrandInfo;
  brandTo: string;
  nav: NavEntry[];
  settingsTo?: string;
  collapsed: boolean;
  hideBrand?: boolean;
  signOutRedirect?: string;
}

export function Sidebar({
  brand,
  brandTo,
  nav,
  settingsTo,
  collapsed,
  hideBrand = false,
  signOutRedirect = '/auth/login',
}: Props): JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const clearSession = useAuth((s) => s.clearSession);
  const perms = user?.effective_permissions ?? [];
  const isAdmin = user?.is_platform_admin ?? false;
  const visible = filterVisible(nav, perms, isAdmin);

  const onSignOut = async (): Promise<void> => {
    try {
      await logout();
    } finally {
      clearSession();
      navigate(signOutRedirect, { replace: true });
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {hideBrand ? null : <Brand brand={brand} to={brandTo} collapsed={collapsed} />}
      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-2">
        <ul className="flex flex-col gap-1">
          {visible.map((entry) => (
            <li key={entry.id}>
              {entry.children ? (
                <SidebarGroup
                  entry={entry}
                  visibleChildren={entry.children}
                  collapsed={collapsed}
                />
              ) : (
                <SidebarLink entry={entry} collapsed={collapsed} />
              )}
            </li>
          ))}
        </ul>
      </nav>
      <div className="shrink-0 border-t border-border bg-background">
        {settingsTo ? (
          <div className="px-2 pt-2">
            <SidebarLink
              entry={{
                id: 'settings',
                to: settingsTo,
                labelKey: 'nav.settings',
                icon: SettingsIcon,
              }}
              collapsed={collapsed}
            />
          </div>
        ) : null}
        <div className="flex items-center gap-2 px-3 py-3">
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.display_name ?? user.email ?? ''}
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold uppercase text-primary"
            >
              {(user?.email ?? '?').charAt(0)}
            </span>
          )}
          {collapsed ? null : (
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground" title={user?.email ?? ''}>
                {user?.display_name ?? user?.email ?? '—'}
              </p>
              <p
                data-testid="header-email"
                className="truncate text-[10px] text-muted-foreground"
                title={user?.email ?? ''}
              >
                {user?.email ?? ''}
              </p>
            </div>
          )}
          <Tooltip label={t('nav.sign_out')}>
            <button
              type="button"
              onClick={() => void onSignOut()}
              aria-label={t('nav.sign_out')}
              data-testid="logout"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <LogOutIcon size={16} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
